import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "@clerk/react";
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { SpinnerGap } from "@phosphor-icons/react";
import { useApi, useListIntegrations, useListUsers } from "./components.js";
import type { ActionResult, ContextualAction } from "scantling-common";

interface ActionMenuItemProps {
  action: ContextualAction;
  onActionSuccess?: (action: ContextualAction, result: ActionResult) => void;
  onActionError?: (action: ContextualAction, error: Error) => void;
  getIcon?: (action: ContextualAction) => ReactNode;
}

export function ActionMenuItem(props: ActionMenuItemProps) {
  return props.action.argument ? (
    <ArgumentSubmenu {...props} />
  ) : (
    <SingleActionMenuItem {...props} />
  );
}

function useExecuteAction() {
  const { getToken } = useAuth();
  const pool = useApi();
  return useCallback(
    async (action: ContextualAction, params: Record<string, unknown>) => {
      const api = pool.acquire();
      try {
        const token = await getToken();
        if (token === null) {
          throw new Error("Not authenticated");
        }
        const authedApi = await api.authenticate(token);
        const orgApi = authedApi.org;
        if (!orgApi) {
          throw new Error("No org available");
        }
        const result = await orgApi.executeAction({
          verb: action.verb,
          params,
        });
        if (!result.success) {
          throw new Error(result.error ?? "Action failed");
        }
        return result;
      } finally {
        pool.release();
      }
    },
    [pool, getToken],
  );
}

function SingleActionMenuItem({
  action,
  onActionSuccess,
  onActionError,
  getIcon,
}: ActionMenuItemProps) {
  const executeAction = useExecuteAction();
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(async () => {
    setLoading(true);
    try {
      const result = await executeAction(action, action.params);
      onActionSuccess?.(action, result);
    } catch (err) {
      onActionError?.(
        action,
        err instanceof Error ? err : new Error(String(err)),
      );
    } finally {
      setLoading(false);
    }
  }, [action, executeAction, onActionSuccess, onActionError]);

  return (
    <DropdownMenuItem
      onClick={handleClick}
      disabled={loading}
      className={
        action.variant === "destructive"
          ? "text-destructive focus:text-destructive"
          : ""
      }
    >
      {loading ? <SpinnerGap className="animate-spin" /> : getIcon?.(action)}
      {action.label}
    </DropdownMenuItem>
  );
}

function ArgumentSubmenu({
  action,
  onActionSuccess,
  onActionError,
  getIcon,
}: ActionMenuItemProps) {
  const { argument } = action;
  if (!argument) return null;

  const executeAction = useExecuteAction();
  const [loadingValue, setLoadingValue] = useState<string | null>(null);

  const integrationsResult = useListIntegrations(
    argument.type === "integration" ? argument.query : undefined,
  );
  const usersResult = useListUsers(
    argument.type === "user" ? argument.query : undefined,
  );

  const options = useMemo(() => {
    if (argument.type === "integration") {
      return (integrationsResult.data?.entries ?? []).map(i => ({
        value: i.slug,
        label: i.niceName ?? i.slug,
      }));
    }
    if (argument.type === "user") {
      return (usersResult.data?.entries ?? []).map(u => ({
        value: u.id,
        label:
          u.firstName && u.lastName
            ? `${u.firstName} ${u.lastName}`
            : (u.email ?? u.id),
      }));
    }
    return [];
  }, [argument.type, integrationsResult.data, usersResult.data]);

  const handleSelect = useCallback(
    async (selectedValue: string) => {
      const mergedParams = {
        ...action.params,
        [argument.key]: selectedValue,
      };
      const mergedAction: ContextualAction = {
        ...action,
        params: mergedParams,
      };
      setLoadingValue(selectedValue);
      try {
        const result = await executeAction(mergedAction, mergedParams);
        onActionSuccess?.(mergedAction, result);
      } catch (err) {
        onActionError?.(
          mergedAction,
          err instanceof Error ? err : new Error(String(err)),
        );
      } finally {
        setLoadingValue(null);
      }
    },
    [action, argument, executeAction, onActionSuccess, onActionError],
  );

  const isLoading =
    (argument.type === "integration" && integrationsResult.isLoading) ||
    (argument.type === "user" && usersResult.isLoading);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        className={
          action.variant === "destructive"
            ? "text-destructive focus:text-destructive"
            : ""
        }
      >
        {getIcon?.(action)}
        {action.label}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        {isLoading ? (
          <DropdownMenuItem disabled>Loading...</DropdownMenuItem>
        ) : options.length === 0 ? (
          <DropdownMenuItem disabled>No options available</DropdownMenuItem>
        ) : (
          options.map(opt => (
            <DropdownMenuItem
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              disabled={loadingValue !== null}
            >
              {loadingValue === opt.value ? (
                <SpinnerGap className="animate-spin" />
              ) : null}
              {opt.label}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
